
import Vue from "vue";
import Incident, { UIIncidentSeverity } from "@/features/Incidents/Incident";
import { DataTableOptions, PagingParameter } from "@/shared/models/PagingParameter";
import { IncidentResponse } from "@/features/Incidents/IncidentService";
import AppError from "@/shared/components/AppError.vue";
import { formatDateWithTimestamp, parseDateAsUTC } from "@/shared/utils/common_utils";

export default Vue.extend({
    name: "IncidentList",
    components: {
        AppError,
    },
    props: {
        // TODO #3738210: Include date range props. Watch for when they change to fetch scorecards with date range filters.
        supplierID: {
            type: String,
            required: false,
            default: "",
        },
        incidentListOptions: {
            default: {
                sortBy: ["id"],
                sortDesc: [false],
                itemsPerPage: 10,
                page: 1,
            } as DataTableOptions,
            required: true,
        },
        beforeDate: {
            default: new Date().toISOString(),
            required: true,
        },
        afterDate: {
            default: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString(), // one year ago today
            required: true,
        },
    },
    data: () => ({
        uiIncidentSeverity: UIIncidentSeverity,
        headers: [
            {
                text: "ID",
                value: "id",
                sortable: true,
                align: "start",
            },
            {
                text: "Date",
                value: "incidentDate",
                sortable: true,
                align: "start",
            },
            {
                text: "Created by",
                value: "createdBy.name",
                sortable: true,
                align: "start",
            },
            {
                text: "Severity",
                value: "severity",
                sortable: true,
                align: "start",
            },
            {
                text: "Description",
                value: "description",
                sortable: true,
                align: "start",
            },
        ],
        incidents: [] as Incident[],
        totalIncidents: 0,
        loading: true,
        errorMessage: "",
        showError: false,
    }),
    watch: {
        incidentListOptions: {
            handler() {
                this.getIncidents();
            },
            deep: true,
        },
        supplierID() {
            this.getIncidents();
        },
        afterDate(newValue, oldValue) {
            // Only re-fetch if the days have changed; disregard any changes to the time
            if (new Date(newValue).toDateString() != new Date(oldValue).toDateString()) {
                this.getIncidents();
            }
        },
        beforeDate(newValue, oldValue) {
            // Only re-fetch if the days have changed; disregard any changes to the time
            if (new Date(newValue).toDateString() != new Date(oldValue).toDateString()) {
                this.getIncidents();
            }
        },
    },
    created: function () {
        this.getIncidents();
    },
    methods: {
        formatDateWithTimestampForTable(assessmentDate: string): string {
            const date = parseDateAsUTC(assessmentDate);
            return formatDateWithTimestamp(date);
        },
        formatPercentageForTable(float: number): string {
            const result = (float * 100).toFixed(0);
            return (result + "%") as string;
        },
        async getIncidents(): Promise<void> {
            try {
                if (this.supplierID) {
                    this.loading = true;
                    const pagingParam = new PagingParameter(this.incidentListOptions, this.afterDate, this.beforeDate);
                    var response = (await this.$incidentService.getIncidentsFromServer({
                        ...pagingParam,
                        supplierID: this.supplierID,
                    })) as IncidentResponse;
                    this.incidents = response.incidents as Incident[];
                    this.totalIncidents = response.totalIncidents;
                }
            } catch (e) {
                this.errorMessage = "Failed to fetch supplier incident data";
                this.showError = true;
            } finally {
                this.loading = false;
            }
        },
        handleChangeIncidentTableOptions(newOptions: DataTableOptions) {
            this.$emit("incident-sort-updated", newOptions);
        },
        onListItemClicked(incidentID: string) {
            this.$emit("incident-list-item-clicked", incidentID);
        },
    },
});
